import CardContentLoadingComponent from 'crm-cards-sdk/components/skeletons/CardContentLoadingComponent';
import ChickletCardContentSkeleton from '../../chicklets/Skeleton';
import { useCardContext } from '../../context/CardContext';
import { CardEmptyState } from '../../extensions/constants/BehaviorTypes';
import { useBehavior } from '../../extensions/hooks/useBehavior';
import AssociationsActionBar from './actionBar/AssociationsActionBar';
import AssociationChiclet from './chiclet/AssociationChiclet';
import { useCardRenderMarkers } from 'crm-cards-sdk/hooks/useCardRenderMarkers';
import { useMemo, useRef, useEffect } from 'react';
import CardFooter from './CardFooter';
import { View } from 'foundations-components/transitional/components/View';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import CardSearchEmptyState from './CardSearchEmptyState';
import styled from 'styled-components';
import InfiniteScrollSentinel from './InfiniteScrollSentinel';
import { PAGE_LENGTH } from '../../hooks/useFetchAssociatedObjectsCardRedesignData';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SCROLL_CONTAINER_HEIGHT = 412;
const ResponsiveGrid = styled.div.withConfig({
  displayName: "CardContent__ResponsiveGrid",
  componentId: "sc-125mu7j-0"
})(["display:grid;grid-template-columns:repeat(auto-fit,minmax(224px,1fr));column-gap:", ";"], props => props.theme.space['300']);
function CardContent() {
  const {
    cardId,
    currentSearchQuery,
    fromObjectId,
    fromObjectTypeId,
    hasScope,
    hasMore,
    isUngated,
    isLoading,
    objectTypeDefinition,
    onViewMore,
    propertyNames,
    results,
    setScrollContainerRef,
    toObjectTypeId,
    total
  } = useCardContext();
  const {
    SuccessMarker
  } = useCardRenderMarkers();
  const scrollableContentRef = useRef(null);
  setScrollContainerRef === null || setScrollContainerRef === void 0 || setScrollContainerRef(scrollableContentRef);
  useEffect(() => {
    if (scrollableContentRef.current) {
      scrollableContentRef.current.style.contain = 'layout style';
    }
  }, [results, isLoading]);
  const CardEmptyStateComponent = useBehavior(CardEmptyState, toObjectTypeId, hasScope, isUngated);
  const skeletonCount = useMemo(() => {
    if (!hasMore || isLoading || !results || results.length === 0 || !total) {
      return 0;
    }
    const remaining = total - results.length;
    return Math.min(PAGE_LENGTH, remaining);
  }, [hasMore, isLoading, results, total]);
  const isInitialLoading = isLoading && !results;
  if (isInitialLoading) {
    return /*#__PURE__*/_jsx(CardContentLoadingComponent, {});
  }
  let content;
  if (!results || results.length === 0) {
    if (currentSearchQuery !== '') {
      content = /*#__PURE__*/_jsx(CardSearchEmptyState, {});
    } else {
      var _objectTypeDefinition;
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(CardEmptyStateComponent, {
          cardId: cardId,
          fromObjectTypeId: toObjectTypeId,
          objectId: fromObjectId,
          objectTypeId: fromObjectTypeId,
          hasScope: hasScope,
          isUngated: isUngated,
          numberOfProperties: propertyNames.length,
          toObjectPluralForm: (_objectTypeDefinition = objectTypeDefinition === null || objectTypeDefinition === void 0 ? void 0 : objectTypeDefinition.pluralForm) !== null && _objectTypeDefinition !== void 0 ? _objectTypeDefinition : ''
        }), /*#__PURE__*/_jsx(SuccessMarker, {})]
      });
    }
  } else {
    content = /*#__PURE__*/_jsxs(ResponsiveGrid, {
      children: [results.map(association => /*#__PURE__*/_jsx(AssociationChiclet, {
        association: association
      }, association.objectId)), skeletonCount > 0 && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(InfiniteScrollSentinel, {
          handleIntersectionChange: onViewMore
        }), [...Array(skeletonCount)].map((_, index) => /*#__PURE__*/_jsx(ChickletCardContentSkeleton, {
          cardId: cardId,
          numberOfProperties: propertyNames.length
        }, `skeleton-${index}`))]
      }), /*#__PURE__*/_jsx(SuccessMarker, {})]
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": `associated-objects-card-content-${toObjectTypeId}`,
    children: [/*#__PURE__*/_jsx(AssociationsActionBar, {}), /*#__PURE__*/_jsx(ScrollContainer, {
      scrollDirection: "vertical",
      maxHeight: SCROLL_CONTAINER_HEIGHT,
      autoHeight: true,
      scrollElementRef: scrollableContentRef,
      marginBlock: "400",
      children: /*#__PURE__*/_jsx(View, {
        children: content
      })
    }), /*#__PURE__*/_jsx(CardFooter, {
      fromObjectId: fromObjectId,
      fromObjectTypeId: fromObjectTypeId,
      hasScope: hasScope,
      isUngated: isUngated,
      toObjectTypeId: toObjectTypeId
    })]
  });
}
export default CardContent;